import React, { useState } from 'react';
import { Table, Modal, Button, SelectPicker } from 'rsuite';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { client } from 'utilities/db';
import { RenderIf } from 'utilities/helpers';
import { CustomTable } from 'components/datatable';
import { TextField } from 'components/input';
import { CustomSelectDropdown } from 'components/select/multi';
import { Toasts } from 'components/toasts';
import { useStore } from 'store';
import EmptyInventoryImg from './../../../assets/Images/Empty_inventory.png';
import { Ikon } from 'assets/icons';
import styles from './index.module.css';
import { useParams } from 'react-router-dom';
import { useFetchInventoryProductHook } from 'hooks/inventory/useFetchInventoryProductHook';
import { DatePicker } from 'rsuite';
import CurrencyInput from 'react-currency-input-field';
import { useCountry } from 'contexts/CountryContext';
import { forwardRef } from 'react';

const { Column, HeaderCell, Cell } = Table;

// Wrapper components with proper ref forwarding
const DatePickerWithRef = forwardRef((props, ref) => (
  <DatePicker {...props} ref={ref} />
));
DatePickerWithRef.displayName = 'DatePickerWithRef';

const CurrencyInputWithRef = forwardRef((props, ref) => (
  <CurrencyInput {...props} ref={ref} />
));
CurrencyInputWithRef.displayName = 'CurrencyInputWithRef';

// Update Modal Component
const UpdateModal = ({
  isOpen,
  onClose,
  inventoryData,
  onSubmit,
  updateLoading
}) => {
  const { currencySymbol, dateFormat } = useCountry();
  const pickerFormat = dateFormat === 'DD/MM/YYYY' ? 'dd/MM/yyyy' : dateFormat === 'DD-MM-YYYY' ? 'dd-MM-yyyy' : 'MM/dd/yyyy';
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    setValue
  } = useForm({
    defaultValues: {
      quantity: '',
      low_stock_alert: '',
      unit: '',
      date_of_purchase: '',
      date_of_expiry: '',
      total_price: '',
      notes: ''
    }
  });

  const units = [
    'kg',
    'pounds',
    'grams',
    'ounces',
    'liters',
    'teaspoon',
    'number/pieces',
    'tablespoon',
    'dozen'
  ];

  // Prefill form when inventoryData changes
  React.useEffect(() => {
    if (inventoryData && isOpen) {
      setValue('quantity', inventoryData?.quantity || '');
      setValue('low_stock_alert', inventoryData?.low_stock_alert || '');

      // Handle unit field - use converted_unit if available, otherwise use unit
      const unitValue = inventoryData?.unit;
      setValue('unit', unitValue || '');

      setValue(
        'date_of_purchase',
        inventoryData?.purchase_date
          ? new Date(inventoryData.purchase_date)
          : ''
      );
      setValue(
        'date_of_expiry',
        inventoryData?.best_before ? new Date(inventoryData.best_before) : ''
      );

      // Handle total_price - use raw value without formatting
      setValue('total_price', inventoryData?.total_price || '');

      // Handle notes field
      setValue('notes', inventoryData?.notes || '');
    }
  }, [inventoryData, isOpen, setValue]);

  const handleFormSubmit = (data) => {
    onSubmit(data);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title>Update Inventory Item</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600'
              }}
            >
              Batch: {inventoryData?.batch_number}
            </label>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Quantity *"
                placeholder="Enter quantity"
                name="quantity"
                type="number"
                error={
                  errors?.quantity?.type === 'required'
                    ? 'Quantity is required'
                    : ''
                }
                register={register}
                validation={{
                  required: true
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Low stock alert *"
                placeholder="Enter low stock alert"
                name="low_stock_alert"
                type="number"
                error={
                  errors?.low_stock_alert?.type === 'required'
                    ? 'Low stock alert is required'
                    : errors?.low_stock_alert?.type === 'pattern'
                    ? 'Low stock alert must be a number'
                    : ''
                }
                register={register}
                validation={{
                  required: true,
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Low stock alert must be a number'
                  }
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Controller
                name="unit"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <label
                      style={{
                        marginBottom: '8px',
                        fontSize: '12px',
                        fontWeight: '400'
                      }}
                    >
                      Unit *
                    </label>
                    <SelectPicker
                      {...field}
                      data={units?.map((unit) => ({
                        value: unit,
                        label: unit?.toUpperCase()
                      }))}
                      placeholder="Choose unit"
                      style={{ width: '100%' }}
                      searchable={false}
                      cleanable={false}
                    />
                    {errors?.unit?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '5px'
                        }}
                      >
                        Unit is required
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Controller
                name="total_price"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <>
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400'
                        }}
                      >
                        Total Price *
                      </label>
                      <CurrencyInputWithRef
                        placeholder={`${currencySymbol}10`}
                        allowDecimals={true}
                        className={`form-control`}
                        value={field.value}
                        onValueChange={(val) => {
                          field.onChange(val);
                        }}
                        prefix={currencySymbol}
                        step={10}
                        style={{
                          width: '100%',
                          padding: '15px',
                          borderRadius: '5px',
                          border: '0px solid #ccc'
                        }}
                      />
                    </div>
                    {errors?.total_price?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '8px'
                        }}
                      >
                        Total price is required
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Controller
                name="date_of_purchase"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '12px',
                        gap: '10px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400'
                        }}
                      >
                        Date of purchase *
                      </label>
                      <DatePickerWithRef
                        {...field}
                        format={pickerFormat}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {errors?.date_of_purchase?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '11px',
                          marginTop: '5px'
                        }}
                      >
                        Date of purchase is required
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Controller
                name="date_of_expiry"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '16px',
                        borderRadius: '10px'
                      }}
                    >
                      <label
                        style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          fontWeight: '400',
                          marginBottom: '10px'
                        }}
                      >
                        Date of expiry *
                      </label>
                      <DatePickerWithRef
                        {...field}
                        format={pickerFormat}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {errors?.date_of_expiry?.type === 'required' && (
                      <span
                        style={{
                          color: 'red',
                          fontSize: '11px',
                          marginTop: '5px'
                        }}
                      >
                        Date of expiry is required
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <TextField
              label="Notes (optional)"
              placeholder="Enter any additional notes"
              name="notes"
              error={
                errors?.notes?.type === 'required' ? 'Notes is required' : ''
              }
              register={register}
              validation={{}}
            />
          </div>

          <div
            style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}
          >
            <Button onClick={handleClose} appearance="subtle">
              Cancel
            </Button>
            <Button type="submit" appearance="primary" disabled={updateLoading}>
              {updateLoading ? 'Updating...' : 'Update Item'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export const ViewItemDetails = () => {
  const { id } = useParams();
  const { branch } = useStore((state) => state);
  const { currencySymbol } = useCountry();

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);

  const { inventoryData, inventoryLoading, inventoryRefetch } =
    useFetchInventoryProductHook(`inventory/findAllByProduct/${id}`, id);

  const {
    mutate: updateInventoryMutate,
    isError,
    error,
    isSuccess
  } = useMutation((data) => {
    return client.post(`/inventory/modify/${selectedInventoryItem?.id}`, data);
  });

  const handleUpdateClick = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setUpdateModalOpen(true);
  };

  const handleUpdateSubmit = async (formData) => {
    try {
      setUpdateLoading(true);

      const payload = {
        quantity: Number(formData.quantity),
        low_stock_alert: Number(formData.low_stock_alert),
        unit: formData.unit?.value || formData.unit,
        purchase_date: formData.date_of_purchase,
        expiry_date: formData.date_of_expiry,
        total_price: Number(formData.total_price),
        notes: formData.notes || '',
        branch_id: branch?.id,
        product_id: selectedInventoryItem?.product_id
      };

      updateInventoryMutate(payload);
    } catch (error) {
      console.error('Update error:', error);
      Toasts.error('Failed to update inventory item');
      setUpdateLoading(false);
    }
  };

  // Handle success/error responses
  React.useEffect(() => {
    if (isSuccess) {
      Toasts.success('Inventory item updated successfully');
      inventoryRefetch();
      setUpdateModalOpen(false);
      setSelectedInventoryItem(null);
      setUpdateLoading(false);
    }
  }, [isSuccess, inventoryRefetch]);

  React.useEffect(() => {
    if (isError) {
      Toasts.error(
        error?.response?.data?.message?.[1]?.msg ||
          error?.response?.data?.message ||
          'Failed to update inventory item'
      );
      setUpdateLoading(false);
    }
  }, [isError, error]);

  const filteredData = inventoryData?.inventories?.filter((item) => {
    return item != null;
  });

  const product = inventoryData?.product;

  return (
    <>
      <header className={styles.header}>
        <div>
          <h2>Inventory</h2>
          {/* <h5>We hope you're having a good day.</h5> */}
        </div>
      </header>
      <div className={styles.content}>
        <RenderIf isTrue={filteredData?.length === 0}>
          <div className={styles.empty_state}>
            <h2>You currently have no inventory for selected item</h2>
            <picture>
              <source srcSet={EmptyInventoryImg} />
              <img src={EmptyInventoryImg} alt={`Empty Inventory`} />
            </picture>
          </div>
        </RenderIf>
        <RenderIf isTrue={filteredData?.length > 0}>
          <div className={styles.rowEqCol}>
            <div>
              <button
                className={`${styles.statBx} ${styles.activeStat}`}
                onClick={() => {}}
              >
                <div>
                  <div className={styles.statIcon}>
                    <Ikon name={`all-products`} fillColor={`var(--white)`} />
                  </div>
                </div>
                <div>
                  <div className={styles.statLabel}>Product Name:</div>
                  <div className={styles.statValue}>
                    {product?.product_name}
                  </div>
                </div>
              </button>
            </div>
          </div>
          <div style={{ marginTop: 24 }}></div>
          <div className={styles.contentBx}>
            <CustomTable
              rows={filteredData?.map((inv, index) => {
                return {
                  id: index + 1,
                  batch_number: inv?.batch_number || '-',
                  quantity: inv?.quantity || '-',
                  unit: inv?.unit || '-',
                  total_price:
                    currencySymbol + parseFloat(inv?.total_price)?.toFixed(4) || '-',
                  purchase_date: inv?.purchase_date || '-',
                  best_before: inv?.best_before || '-',
                  converted_quantity: inv?.converted_quantity || '-',
                  converted_unit: inv?.converted_unit || '-',
                  unit_price:
                    currencySymbol + parseFloat(inv?.unit_price)?.toFixed(4) || '-'
                };
              })}
              loading={inventoryLoading}
              rowHeight={45}
              pageLimit={10}
              pagination={filteredData?.length > 10 ? true : false}
            >
              <Column width={50} fixed>
                <HeaderCell>Id</HeaderCell>
                <Cell dataKey="id" />
              </Column>
              <Column flexGrow={1} sortable>
                <HeaderCell>Batch</HeaderCell>
                <Cell dataKey="batch_number" />
              </Column>
              <Column width={100} sortable>
                <HeaderCell>Quantity</HeaderCell>
                <Cell dataKey="quantity" />
              </Column>
              <Column width={120} sortable>
                <HeaderCell>Unit</HeaderCell>
                <Cell dataKey="unit" />
              </Column>
              <Column width={120} sortable>
                <HeaderCell>Total Price</HeaderCell>
                <Cell dataKey="total_price" />
              </Column>
              <Column width={100} sortable>
                <HeaderCell>Purchase Date</HeaderCell>
                <Cell dataKey="purchase_date" />
              </Column>
              <Column width={100} sortable>
                <HeaderCell>Best Before</HeaderCell>
                <Cell dataKey="best_before" />
              </Column>
              <Column width={160} sortable>
                <HeaderCell>Converted Quantity</HeaderCell>
                <Cell dataKey="converted_quantity" />
              </Column>
              <Column width={110} sortable>
                <HeaderCell>Converted Unit</HeaderCell>
                <Cell dataKey="converted_unit" />
              </Column>
              <Column width={110} sortable>
                <HeaderCell>Unit Price</HeaderCell>
                <Cell dataKey="unit_price" />
              </Column>
              <Column width={100}>
                <HeaderCell>Actions</HeaderCell>
                <Cell dataKey="action" style={{ textAlign: 'center' }}>
                  {(rowData, rowIndex) => {
                    const inventoryItem = filteredData[rowIndex];
                    return (
                      <div
                        style={{
                          color: 'var(--primary)',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          border: '1px solid var(--primary)',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          textAlign: 'center'
                        }}
                        onClick={() => handleUpdateClick(inventoryItem)}
                      >
                        Update
                      </div>
                    );
                  }}
                </Cell>
              </Column>
            </CustomTable>
          </div>
        </RenderIf>
      </div>

      {/* Update Modal */}
      <UpdateModal
        isOpen={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setSelectedInventoryItem(null);
        }}
        inventoryData={selectedInventoryItem}
        onSubmit={handleUpdateSubmit}
        updateLoading={updateLoading}
      />
    </>
  );
};
